import React, { useState } from "react";
import Table from "component/table";
import Pagination from "component/pagination";
import notFoundImg from "asset/images/not-found.svg";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Input from "component/form/Input";
import MoreOptionsDropdown from "./modals/MoreOptionsDropdown";
import { shipmentStatus } from "constants/global";
import { formatDate } from "../utils";
import getInitials from "utilities/getInitials";
import RecordPayment from "../RecordPayment";
import CancelShipmentModal from "./components/modals/CancelShipmentModal";
import { Bars } from "react-loader-spinner";
import UpdateStatusModal from "./components/modals/UpdateStatusModal";
import { useMutation, useQueryClient } from "react-query";
import { handleDuplicateShipment } from "api/fetchers";
import { toast } from "react-toastify";
import { SearchInput } from "../../../component/TableSearchInput";

export const UserInitials = (user) => (
    <div className="flex items-center gap-x-2">
        <div className="flex items-center justify-center p-2 bg-[#EFF0FB] text-[#475467] rounded-full size-10">
            {getInitials(user)}
        </div>{" "}
        <span>{user}</span>
    </div>
);

const ShipmentTableHeader = [
    { title: "Tracking Number", width: "190px" },
    { title: "Origin Point", width: "230px" },
    { title: "Destination Point", width: "230px" },
    { title: "Shipment Status", width: "190px" },
    { title: "Receiver", width: "220px" },
    { title: "Date", width: "210px" },
];

const ShipmentTable = ({
    paginatedShipments,
    currentPage,
    setCurrentPage,
    pageSize,
    tabs,
    activeTab,
    setActiveTab,
    subTabs,
    shipmentSubTab,
    setShipmentSubTab,
    searchTerm,
    setSearchTerm,
    isLoading,
    refetchShipments,
    notActiveBusiness,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [recordPaymentModal, setRecordPaymentModal] = useState(false);
    const [selectedShipment, setSelectedShipment] = useState(null);
    const [cancelShipmentModal, setShowCancelShipmentModal] = useState(false);
    const [showModalUpdateStatusModal, setShowModalUpdateStatusModal] = useState(false);
    const queryClient = useQueryClient();

    const { mutateAsync: handleDuplicateFunc } = useMutation({
        mutationFn: (shipmentId) => {
            handleDuplicateShipment(shipmentId);
        },
        mutationKey: "duplicate-shipment",
        onSuccess: async (data) => {
            await refetchShipments();
            toast.success("Shipment duplicated successfully");
        },
        onError: (error) => {
            toast.error(error?.message || "Error duplicating shipment");
        },
    });

    const shipmentOptions = (shipment) => [
        {
            label: "Record payment",
            value: "record-payment",
            action: () => {
                setSelectedShipment(shipment);
                setRecordPaymentModal(true);
            },
        },
        {
            label: "Update status",
            value: "update-status",
            action: () => {
                setSelectedShipment(shipment);
                setShowModalUpdateStatusModal(true);
            },
        },
        {
            label: "Edit shipment",
            value: "edit-shipment",
            action: () => {
                navigate(`/flex/shipments/create?edit=${shipment?.id}`, {
                    state: { shipmentData: shipment },
                });
            },
        },
        {
            label: "Duplicate this shipment",
            value: "duplicate-shipment",
            action: () => {
                // navigate(`/flex/shipments/create?edit=${shipment?.id}`, {
                //     state: { shipmentData: shipment },
                // });
                handleDuplicateFunc(shipment?._id);
            },
        },
        {
            label: "Cancel shipment",
            value: "cancel-shipment",
            className: "text-red-500",
            action: () => handleCancelShipment(shipment),
        },
    ];

    const handleSubTabClick = (text, endpoint) => {
        setShipmentSubTab(endpoint);
        setCurrentPage(1);
    };

    const handleAction = (actionName, shipment) => {
        // TODO: Implement functionality for each action
        if (actionName === "Record Payment") {
            setRecordPaymentModal(true);
        }
    };

    const handleCancelShipment = (shipmentDetails) => {
        setSelectedShipment(shipmentDetails);
        setShowCancelShipmentModal(true);
    };

    const handleRowClick = (shipmentId) => {
        activeTab === "Created"
            ? navigate(`/flex/home/manage-shipment?shipmentId=${shipmentId}`)
            : navigate(`/flex/home/manage-shipment?shipmentId=${shipmentId}&userView=assigned`);
    };

    return (
        <>
            <div>
                <div className="flex gap-x-3 md:px-6">
                    {tabs.map((tab) => (
                        <button
                            key={tab}
                            className={`relative px-1 py-2 text-sm font-[450] transition-all duration-300 ${
                                activeTab === tab
                                    ? "after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-full after:bg-[#6F00FF] after:rounded-lg"
                                    : "text-gray-500"
                            }`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
                <div className="pb-10 bg-white rounded-xl min-h-[60vh]">
                    {/* Main Tabs */}
                    <div className="p-4">
                        {/* Sub Tabs and Search */}
                        <div className="flex flex-col items-center justify-between gap-x-3 md:flex-row">
                            <div className="flex w-full gap-2 p-1 mb-2 overflow-x-auto rounded-lg md:overflow-hidden md:w-7/12 sm:mb-0">
                                {subTabs.map(({ text, endpoint }) => (
                                    <button
                                        key={text}
                                        className={`rounded py-1 sm:py-2 px-3 text-sm font-medium transition-all whitespace-nowrap duration-300 ${
                                            shipmentSubTab === endpoint
                                                ? "text-[#6F00FF] bg-[#EBE4FF]"
                                                : "text-main-grey hover:shadow-sm"
                                        }`}
                                        onClick={() => handleSubTabClick(text, endpoint)}
                                    >
                                        {text}
                                    </button>
                                ))}
                            </div>
                            {/* {filteredShipments?.length > 0 && ( */}
                            <SearchInput
                                placeholder="Search by name, business, etc."
                                // value={searchTerm}
                                onChange={(value) => setSearchTerm(value)}
                                // className="w-full max-md:max-w-[446px] sm:max-w-[396px]"
                            />
                            {/* )} */}
                        </div>
                    </div>

                    {isLoading ? (
                        <Bars
                            wrapperClass="w-max mx-auto mt-[100px] mb-[160px]"
                            height={30}
                            width={30}
                            color="#6F00FF"
                        />
                    ) : (
                        <>
                            {paginatedShipments?.length > 0 ? (
                                activeTab === "Created" ? (
                                    <>
                                        <Table
                                            titles={ShipmentTableHeader}
                                            rows={paginatedShipments?.map((shipment) => {
                                                return {
                                                    columns: [
                                                        {
                                                            value: (
                                                                <div className="flex flex-col items-start justify-center">
                                                                    <p className="text-sm font-semibold">
                                                                        {shipment.trackingNumber}
                                                                    </p>
                                                                    <p className="text-xs font-light">
                                                                        {shipment?.shipmentTitle
                                                                            ? shipment?.shipmentTitle
                                                                            : "N/A"}
                                                                    </p>
                                                                </div>
                                                            ),
                                                            className: "text-main-dark",
                                                        },
                                                        {
                                                            value: (
                                                                <div className="flex items-center">
                                                                    {shipment.origin}
                                                                </div>
                                                            ),
                                                            className: "text-main-dark",
                                                        },
                                                        {
                                                            value: (
                                                                <div className="flex items-center">
                                                                    {shipment.destination}
                                                                </div>
                                                            ),
                                                            className: "text-main-dark",
                                                        },
                                                        {
                                                            value: (
                                                                <span
                                                                    style={{
                                                                        backgroundColor:
                                                                            shipmentStatus[
                                                                                shipment?.status
                                                                            ]?.bgColor || "#DBDFE5",
                                                                        color:
                                                                            shipmentStatus[
                                                                                shipment?.status
                                                                            ]?.textColor ||
                                                                            "#B64607",
                                                                    }}
                                                                    className={`rounded-xl py-1 px-2 w-auto text-xs font-normal`}
                                                                >
                                                                    {
                                                                        shipmentStatus[
                                                                            shipment.status
                                                                        ]?.text
                                                                    }
                                                                </span>
                                                            ),
                                                            className: "text-main-grey",
                                                        },
                                                        {
                                                            value: shipment?.receiver ? (
                                                                <div className="flex items-center gap-x-2">
                                                                    <div className="flex items-center justify-center p-2 bg-[#EFF0FB] text-[#475467] rounded-full size-10">
                                                                        {getInitials(
                                                                            shipment?.receiver
                                                                        )}
                                                                    </div>{" "}
                                                                    {shipment?.receiver}
                                                                </div>
                                                            ) : (
                                                                "N/A"
                                                            ),
                                                            className: "text-main-dark",
                                                        },

                                                        {
                                                            value: (
                                                                <div className="flex items-center w-full gap-14">
                                                                    <span className="text-main-grey">
                                                                        {formatDate(
                                                                            shipment?.date,
                                                                            "MMM D YYYY"
                                                                        )}
                                                                    </span>
                                                                    <MoreOptionsDropdown
                                                                        options={shipmentOptions(
                                                                            shipment
                                                                        )}
                                                                    />
                                                                </div>
                                                            ),
                                                            className: "text-main-grey",
                                                        },
                                                    ],
                                                    rowId: shipment.id,
                                                    onRowClick: () => handleRowClick(shipment.id),
                                                };
                                            })}
                                            wrapperClassName="shadow-none"
                                        />

                                        <Pagination
                                            currentPage={currentPage}
                                            setCurrentPage={setCurrentPage}
                                            totalPages={pageSize}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <Table
                                            titles={ShipmentTableHeader}
                                            rows={paginatedShipments?.map((shipment) => {
                                                return {
                                                    columns: [
                                                        {
                                                            value: (
                                                                <div className="flex flex-col items-start justify-center">
                                                                    <p className="text-sm font-semibold">
                                                                        {shipment.trackingNumber}
                                                                    </p>
                                                                    <p className="text-xs font-light">
                                                                        {shipment?.shipmentTitle
                                                                            ? shipment?.shipmentTitle
                                                                            : "N/A"}
                                                                    </p>
                                                                </div>
                                                            ),
                                                            className: "text-main-dark",
                                                        },
                                                        {
                                                            value: (
                                                                <div className="flex items-center">
                                                                    {shipment.origin}
                                                                </div>
                                                            ),
                                                            className: "text-main-dark",
                                                        },
                                                        {
                                                            value: (
                                                                <div className="flex items-center">
                                                                    {shipment.destination}
                                                                </div>
                                                            ),
                                                            className: "text-main-dark",
                                                        },
                                                        {
                                                            value: (
                                                                <span
                                                                    style={{
                                                                        backgroundColor:
                                                                            shipmentStatus[
                                                                                shipment?.status
                                                                            ]?.bgColor || "#DBDFE5",
                                                                        color:
                                                                            shipmentStatus[
                                                                                shipment?.status
                                                                            ]?.textColor ||
                                                                            "#B64607",
                                                                    }}
                                                                    className={`rounded-xl py-1 px-2 w-auto text-xs font-normal`}
                                                                >
                                                                    {
                                                                        shipmentStatus[
                                                                            shipment.status
                                                                        ]?.text
                                                                    }
                                                                </span>
                                                            ),
                                                            className: "text-main-grey",
                                                        },
                                                        {
                                                            value: shipment?.receiver ? (
                                                                <div className="flex items-center gap-x-2">
                                                                    <div className="flex items-center justify-center p-2 bg-[#EFF0FB] text-[#475467] rounded-full size-10">
                                                                        {getInitials(
                                                                            shipment?.receiver
                                                                        )}
                                                                    </div>{" "}
                                                                    {shipment?.receiver}
                                                                </div>
                                                            ) : (
                                                                "N/A"
                                                            ),
                                                            className: "text-main-dark",
                                                        },

                                                        {
                                                            value: (
                                                                <div className="flex items-center w-full gap-14">
                                                                    <span className="text-main-grey">
                                                                        {formatDate(
                                                                            shipment?.date,
                                                                            "MMM D YYYY"
                                                                        )}
                                                                    </span>
                                                                    <MoreOptionsDropdown
                                                                        options={shipmentOptions(
                                                                            shipment
                                                                        )}
                                                                    />
                                                                </div>
                                                            ),
                                                            className: "text-main-grey",
                                                        },
                                                    ],
                                                    rowId: shipment.id,
                                                    onRowClick: () => handleRowClick(shipment.id),
                                                };
                                            })}
                                            wrapperClassName="shadow-none"
                                        />

                                        <Pagination
                                            currentPage={currentPage}
                                            setCurrentPage={setCurrentPage}
                                            totalPages={pageSize}
                                        />
                                    </>
                                )
                            ) : (
                                <div className="flex flex-col items-center mt-10">
                                    <img
                                        src={notFoundImg}
                                        alt="No shipments found"
                                        className="w-[144px] h-[107px]"
                                    />
                                    <p className="mt-5 mb-6 text-sm font-medium text-main-dark">
                                        You don’t have any shipments yet
                                    </p>
                                    <Link
                                        to={notActiveBusiness ? "/flex/shipments/create" : "#"}
                                        className={`bg-[#6F00FF] text-white py-2 px-4 rounded-lg ${
                                            !notActiveBusiness
                                                ? "opacity-50 cursor-not-allowed"
                                                : ""
                                        }`}
                                    >
                                        Create new shipment
                                    </Link>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            {recordPaymentModal && (
                <RecordPayment
                    order={selectedShipment}
                    orderId={selectedShipment?.id}
                    totalAmount={selectedShipment?.totalAmount}
                    close={() => setRecordPaymentModal(false)}
                    type="shipment"
                    refetchData={() => {
                        queryClient.invalidateQueries({ queryKey: ["get-all-shipments"] });
                        setRecordPaymentModal(false);
                    }}
                />
            )}

            {cancelShipmentModal && (
                <CancelShipmentModal
                    shipmentDetails={selectedShipment}
                    show={cancelShipmentModal}
                    onClose={() => setShowCancelShipmentModal(false)}
                    refetchData={() => {
                        queryClient.invalidateQueries({ queryKey: ["get-all-shipments"] });
                        setShowCancelShipmentModal(false);
                    }}
                />
            )}

            {showModalUpdateStatusModal && (
                <UpdateStatusModal
                    show={showModalUpdateStatusModal}
                    shipmentId={selectedShipment?._id}
                    shipmentDetails={selectedShipment?.shippingDetails}
                    refetchData={() => {
                        queryClient.invalidateQueries({ queryKey: ["get-all-shipments"] });
                        setShowModalUpdateStatusModal(false);
                    }}
                    onHide={() => setShowModalUpdateStatusModal(false)}
                />
            )}
        </>
    );
};

export default ShipmentTable;
